import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Cropper from "react-easy-crop";
import { Modal, Button, Alert, Spinner } from "react-bootstrap";
import { cropImage, blobToFile } from "../utils/cropImage";
import "../styles/ImageCropModal.css";

// The frame has a fixed size on screen and at the start (zoom 1) it represents
// exactly expectedWidth x expectedHeight pixels of the original image.
//
// Zoom moves the IMAGE under the frame:
//  - Zoom IN (> 1): the frame covers fewer image pixels, so the result is
//    enlarged to the required size and may look blurry (a note is shown).
//  - Zoom OUT (< 1): the frame covers more image pixels, so a large image can
//    be fitted into the frame. The result is scaled down to the required size
//    by cropImage, so there is no quality loss. Zooming out stops when the
//    frame covers the whole image (the image never leaves empty space in it).
const MAX_ZOOM = 4; // zoom 4 = +400% on the zoom label

const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

const ImageCropModalContent = ({
    show,
    imageSrc,
    fileName,
    mimeType,
    expectedWidth,
    expectedHeight,
    onCancel,
    onCropComplete,
    optional = false,
}) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [areaPixels, setAreaPixels] = useState(null);
    const [imgDims, setImgDims] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Cropper container measurement (callback ref works inside the Modal portal)
    const [wrapEl, setWrapEl] = useState(null);
    const [wrapSize, setWrapSize] = useState(null);

    // Read the image's natural dimensions
    useEffect(() => {
        if (!show || !imageSrc) return;

        let cancelled = false;
        const img = new Image();

        img.onload = () => {
            if (!cancelled) {
                setImgDims({ w: img.naturalWidth, h: img.naturalHeight });
            }
        };

        img.onerror = () => {
            if (!cancelled) {
                setError("Failed to load the uploaded image.");
            }
        };

        img.src = imageSrc;

        return () => {
            cancelled = true;
        };
    }, [show, imageSrc]);

    // Track the container size (rounded, and only updates when it really changes)
    useEffect(() => {
        if (!wrapEl) return;
        const ro = new ResizeObserver(([entry]) => {
            const w = Math.round(entry.contentRect.width);
            const h = Math.round(entry.contentRect.height);
            setWrapSize((prev) =>
                prev && prev.w === w && prev.h === h ? prev : { w, h }
            );
        });
        ro.observe(wrapEl);
        return () => ro.disconnect();
    }, [wrapEl]);

    // Receives (areaPercentages, areaPixels) from the cropper
    const handleAreaChange = useCallback((_areaPct, pixels) => {
        console.log("area pixels", pixels);
        setAreaPixels((prev) =>
            prev &&
                prev.x === pixels.x &&
                prev.y === pixels.y &&
                prev.width === pixels.width &&
                prev.height === pixels.height
                ? prev
                : pixels
        );
        setError((e) => (e === null ? e : null));
    }, []);

    const handleReset = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setError(null);
    };

    const d = gcd(expectedWidth, expectedHeight) || 1;
    const ratioLabel = `${expectedWidth / d}:${expectedHeight / d}`;
    const aspect = expectedWidth / expectedHeight;

    // The whole image is shown fitted in the container (never enlarged), so one
    // image pixel is k screen pixels. The frame must represent expectedWidth x
    // expectedHeight image pixels, so on screen it is that size times k.
    // It stays this size whatever the zoom or rotation.
    const { cropSize, tooSmall } = useMemo(() => {
        if (!imgDims || !wrapSize || !wrapSize.w || !wrapSize.h) {
            return { cropSize: null, tooSmall: false };
        }

        const k = Math.min(1, wrapSize.w / imgDims.w, wrapSize.h / imgDims.h);

        return {
            cropSize: { width: expectedWidth * k, height: expectedHeight * k },
            tooSmall: imgDims.w < expectedWidth || imgDims.h < expectedHeight,
        };
    }, [imgDims, wrapSize, expectedWidth, expectedHeight]);

    // Screen scale (screen px per image px). Zoom is relative to the fitted
    // image, so it stays the same when the container resizes. Only the pan
    // offset (in screen px) must be scaled together with k.
    const k = cropSize ? cropSize.width / expectedWidth : null;
    const prevKRef = useRef(null);

    useLayoutEffect(() => {
        if (!k) return;
        const prevK = prevKRef.current;
        if (prevK && prevK !== k) {
            const r = k / prevK;
            setCrop((c) => ({ x: c.x * r, y: c.y * r }));
        }
        prevKRef.current = k;
    }, [k]);

    // Smallest zoom (most zoomed out) at which the unrotated image still covers
    // the whole frame. For a large image this is below 1, which lets the user
    // fit more of the image into the frame. It is never above 1.
    //
    // It does NOT depend on the rotation, so rotating never changes the zoom.
    // The trade-off: at some angles the rotated image may not reach the frame's
    // corners, which then stay empty in the result.
    const minZoom = useMemo(() => {
        if (!imgDims || !cropSize || !k) return 1;

        const coverZoom = Math.max(
            cropSize.width / (imgDims.w * k),
            cropSize.height / (imgDims.h * k)
        );

        return Math.min(1, coverZoom);
    }, [imgDims, cropSize, k]);

    const maxZoom = MAX_ZOOM;
    const safeZoom = Math.min(Math.max(zoom, minZoom), maxZoom);

    // Zoom shown as a change from the start: 0% = start (zoom 1), positive
    // when zoomed in (+400% at MAX_ZOOM), negative when zoomed out.
    const zoomPercent = Math.round(safeZoom * 100);

    // Size of the selected area in the original image's pixels
    const selW = areaPixels ? Math.round(areaPixels.width) : null;
    const isExact = selW !== null && Math.abs(selW - expectedWidth) <= 1;
    const isEnlarged = selW !== null && selW < expectedWidth - 1;

    // Quality = how many original pixels back the required output pixels.
    // Capped at 100% (zoomed out = downscaled = no loss).
    const quality = useMemo(() => {
        if (!areaPixels) return null;

        // Aspect ratio is locked, so width and height give the same ratio;
        // min() is just a safe guard against rounding differences.
        const ratio = Math.min(
            areaPixels.width / expectedWidth,
            areaPixels.height / expectedHeight
        );

        const q = Math.min(100, Math.max(0, ratio * 100));
        return Math.round(q * 10) / 10; // 1 decimal
    }, [areaPixels, expectedWidth, expectedHeight]);

    const lostPercent = quality !== null ? Math.round((100 - quality) * 10) / 10 : null;

    const qualityLevel = useMemo(() => {
        if (quality === null || !isEnlarged) return null;
        if (quality >= 90) return { color: "#15803d", bg: "#f0fdf4", border: "#86efac", label: "Slight quality loss", icon: "ℹ" };
        if (quality >= 75) return { color: "#a16207", bg: "#fefce8", border: "#fde047", label: "Moderate quality loss", icon: "⚠" };
        return { color: "#b91c1c", bg: "#fef2f2", border: "#fca5a5", label: "High quality loss", icon: "⚠" };
    }, [quality, isEnlarged]);

    const handleApply = async () => {
        if (!areaPixels || !imgDims || tooSmall) return;

        setSubmitting(true);
        setError(null);

        try {
            const outType = ["image/jpeg", "image/png", "image/webp"].includes(mimeType)
                ? mimeType
                : "image/png";

            // At actual size, use exactly the required pixels (no resampling at all)
            let pixelCrop = areaPixels;
            if (isExact) {
                const rad = (rotation * Math.PI) / 180;
                const rotW = Math.ceil(
                    Math.abs(imgDims.w * Math.cos(rad)) + Math.abs(imgDims.h * Math.sin(rad))
                );
                const rotH = Math.ceil(
                    Math.abs(imgDims.w * Math.sin(rad)) + Math.abs(imgDims.h * Math.cos(rad))
                );
                const place = (v, max) => Math.min(Math.max(0, Math.round(v)), Math.max(0, max));

                pixelCrop = {
                    x: place(areaPixels.x, rotW - expectedWidth),
                    y: place(areaPixels.y, rotH - expectedHeight),
                    width: expectedWidth,
                    height: expectedHeight,
                };
            }

            const blob = await cropImage(
                imageSrc,
                pixelCrop,
                outType,
                0.95,
                rotation,
                expectedWidth,
                expectedHeight
            );
            const file = blobToFile(blob, fileName);
            const message = await onCropComplete(file);

            if (message) setError(message);
        } catch (e) {
            setError(
                e.message || "Something went wrong while cropping. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    // Remount on size change so the library re-measures the image and container.
    // Zoom is kept in state and the pan offset is rescaled by the effect above.
    const cropperKey = wrapSize ? `${wrapSize.w}x${wrapSize.h}` : "init";

    return (
        <Modal
            show={show}
            onHide={onCancel}
            centered
            backdrop="static"
            keyboard={!submitting}
            size="lg"
            className="crop-modal"
            backdropClassName="crop-modal-backdrop"
            style={{ zIndex: 1500 }}
        >
            <Modal.Header closeButton={!submitting}>
                <Modal.Title
                    style={{ fontSize: "1.15rem", fontWeight: 700, color: "#1e293b" }}
                >
                    Crop Image
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-2 p-sm-3">
                <p style={{ color: "#475569", fontSize: "0.85rem", marginBottom: 10 }}>
                    {optional
                        ? `Your image already fits this field (${ratioLabel}, ${expectedWidth} × ${expectedHeight} px). Crop it to focus on an area, or keep it as it is.`
                        : `This field requires a ${ratioLabel} image (${expectedWidth} × ${expectedHeight} px). Drag the image to adjust its position within the crop frame.`}
                </p>

                {/* Image cropper */}
                <div
                    ref={setWrapEl}
                    className="crop-wrap"
                    style={{
                        position: "relative",
                        width: "100%",
                        height: "clamp(240px, 55vh, 460px)",
                        background: "#0f172a",
                        borderRadius: 8,
                        overflow: "hidden",
                        touchAction: "none",
                    }}
                >
                    {imageSrc && imgDims && cropSize && (
                        <Cropper
                            key={cropperKey}
                            image={imageSrc}
                            crop={crop}
                            zoom={safeZoom}
                            rotation={rotation}
                            minZoom={minZoom}
                            maxZoom={maxZoom}
                            zoomSpeed={0.5}
                            aspect={aspect}
                            cropSize={cropSize}
                            objectFit="contain"
                            showGrid={false}
                            classes={{ cropAreaClassName: "crop-area" }}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={handleAreaChange}
                        />
                    )}
                </div>

                {/* Image editing controls */}
                <div className="crop-controls-wrap mt-3">
                    <div className="crop-controls">
                        <div className="crop-ctrl crop-ctrl-zoom">
                            <label
                                htmlFor="image-zoom"
                                className="crop-ctrl-label mb-0 text-nowrap"
                            >
                                Zoom
                            </label>

                            <input
                                id="image-zoom"
                                type="range"
                                min={minZoom}
                                max={maxZoom}
                                step="any"
                                value={safeZoom}
                                onChange={(e) => {
                                    setZoom(Number(e.target.value));
                                    setError(null);
                                }}
                                disabled={submitting || !imgDims || maxZoom <= minZoom}
                                className="crop-ctrl-slider form-range"
                            />

                            <span className="crop-ctrl-value text-muted text-nowrap">
                                {zoomPercent}%
                            </span>
                        </div>

                        <div className="crop-ctrl crop-ctrl-rotation">
                            <label
                                htmlFor="image-rotation"
                                className="crop-ctrl-label mb-0 text-nowrap"
                            >
                                Rotation
                            </label>

                            <input
                                id="image-rotation"
                                type="range"
                                min={0}
                                max={360}
                                step={1}
                                value={rotation}
                                onChange={(e) => {
                                    setRotation(Number(e.target.value));
                                    setError(null);
                                }}
                                disabled={submitting || !imgDims}
                                className="crop-ctrl-slider form-range"
                            />

                            <span className="crop-ctrl-value text-muted text-nowrap">
                                {Math.round(rotation)}°
                            </span>
                        </div>

                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleReset}
                            disabled={submitting || !imgDims}
                            className="crop-reset text-nowrap"
                        >
                            Reset
                        </Button>
                    </div>
                </div>

                {areaPixels && !tooSmall && isEnlarged && qualityLevel && (
                    <div
                        className="mt-2"
                        style={{
                            fontSize: "0.8rem",
                            color: qualityLevel.color,
                            background: qualityLevel.bg,
                            border: `1px solid ${qualityLevel.border}`,
                            borderRadius: 6,
                            padding: "6px 10px",
                        }}
                    >
                        <strong>
                            {qualityLevel.icon} {qualityLevel.label} ({Math.round(lostPercent)}% lost)
                        </strong>
                        {" · "}
                        The image is enlarged, and the quality of the selected area is reduced. Zoom out for a sharper result.
                    </div>
                )}

                {tooSmall && imgDims && (
                    <Alert
                        variant="warning"
                        className="mt-3 mb-0"
                        style={{ fontSize: "0.85rem" }}
                    >
                        This image is {imgDims.w} × {imgDims.h} px, which is smaller than the
                        required {expectedWidth} × {expectedHeight} px, so it can’t be cropped
                        to the exact size. Please choose a larger image.
                    </Alert>
                )}

                {error && (
                    <Alert
                        variant="danger"
                        className="mt-3 mb-0"
                        style={{ fontSize: "0.85rem", whiteSpace: "pre-line" }}
                    >
                        {error}
                    </Alert>
                )}
            </Modal.Body>

            <Modal.Footer style={{ flexWrap: "wrap", gap: 8 }}>
                <Button
                    variant="outline-secondary"
                    className="flex-fill flex-sm-grow-0"
                    onClick={onCancel}
                    disabled={submitting}
                    style={{ margin: 0, minWidth: 110 }}
                >
                    {optional ? "Keep original" : "Cancel"}
                </Button>

                <Button
                    variant="primary"
                    className="flex-fill flex-sm-grow-0"
                    onClick={handleApply}
                    disabled={!areaPixels || submitting || tooSmall}
                    style={{
                        margin: 0,
                        minWidth: 110,
                        background: "var(--primary-gradient)",
                        border: "none",
                        fontWeight: 600,
                    }}
                >
                    {submitting ? <Spinner animation="border" size="sm" /> : "Apply crop"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// Remounts the editor every time the modal opens or a different image is
// chosen, so zoom, rotation and position always start fresh. Resetting in an
// effect is too late: the first render would still use the previous session's
// values.
const ImageCropModal = (props) => {
    const { show, imageSrc } = props;
    const [session, setSession] = useState(0);
    const [prevShow, setPrevShow] = useState(show);

    if (show !== prevShow) {
        setPrevShow(show);
        if (show) setSession((n) => n + 1);
    }

    return (
        <ImageCropModalContent
            key={`${session}:${imageSrc || ""}`}
            {...props}
        />
    );
};

export default ImageCropModal;